import React, {useState} from 'react';
import {
    Award,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Clock,
    ExternalLink,
    Mail,
    MapPin,
    MessageCircle,
    Phone,
    Star,
    Users
} from 'lucide-react';
import {Header} from "../components/Header";

export const YellowPageEntityPublicProfile = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavorited, setIsFavorited] = useState(false);

    // Sample data based on the registration form structure
    const entityData = {
        type: 'Business Entity',
        subType: 'Both', // Products & Services
        name: 'TechFix Solutions',
        ownerName: 'Rajesh Kumar',
        category: 'Electronics & Electrical',
        area: 'Bhayandar West',
        pinCode: '401101',
        address: 'Shop No. 15, Maxus Mall, Near Railway Station, Bhayandar West',
        mobile: '+91 98765 43210',
        whatsapp: '+91 98765 43210',
        email: 'info@techfixsolutions.com',
        experience: '5-10 years',
        description: 'We sell latest mobile phones, laptops, and electronic gadgets. We also provide professional repair services for all brands of smartphones, laptops, and electronic devices with genuine spare parts and warranty.',
        images: [
            '/assets/tech-filler-1.png',
            '/assets/tech-filler-2.png',
            '/assets/tech-filler-3.png'
        ],
        rating: 4.6,
        reviewCount: 127,
        isVerified: true,
        operatingHours: {
            monday: '9:00 AM - 8:00 PM',
            tuesday: '9:00 AM - 8:00 PM',
            wednesday: '9:00 AM - 8:00 PM',
            thursday: '9:00 AM - 8:00 PM',
            friday: '9:00 AM - 8:00 PM',
            saturday: '9:00 AM - 8:00 PM',
            sunday: '10:00 AM - 6:00 PM'
        },
        services: [
            'Mobile Phone Sales',
            'Laptop Sales',
            'Screen Repair',
            'Battery Replacement',
            'Software Installation',
            'Data Recovery'
        ],
        specialOffers: [
            'Free screen guard with every phone purchase',
            '10% discount on repair services for students',
            'Extended warranty available'
        ]
    };

    const nextImage = () => {
        setCurrentImageIndex((prev) =>
            prev === entityData.images.length - 1 ? 0 : prev + 1
        );
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) =>
            prev === 0 ? entityData.images.length - 1 : prev - 1
        );
    };

    const getCurrentDay = () => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[new Date().getDay()];
    };

    const isOpenNow = () => {
        const currentHours = entityData.operatingHours[getCurrentDay()];
        return currentHours && currentHours !== 'Closed';
    };

    return (
        <div
            className={`min-h-[100dvh] h-[100dvh] max-h-[100dvh]
                min-w-full w-full max-w-full 
                bg-white flex flex-col justify-end`}
        >
            {/* Header */}
            <Header/>

            {/* Main Content */}
            <div
                className="bg-orange-50 font-custom-sans text-gray-800 min-h-screen
                    flex flex-col justify-between thin-scrollbar overflow-y-scroll min-w-full w-full max-w-full

                    portrait:pt-[4.75rem] portrait:sm:pt-[6.25rem] portrait:jk:pt-[5.75rem]

                    portrait:min-h-[100dvh] portrait:h-[100dvh] portrait:max-h-[100dvh]

                    landscape:pt-[3.5rem] landscape:jk:pt-[4.5rem]

                    landscape:min-h-[100dvh] landscape:h-[100dvh] landscape:max-h-[100dvh]

                    portrait:pb-[0.75rem] portrait:pl-[0.75rem] portrait:pr-[0.75rem]
                    landscape:pb-[0.5rem] landscape:pl-[0.5rem] landscape:pr-[0.5rem]

                    portrait:gap-[0.75rem] landscape:gap-[0.5rem]"
            >
                <div className="max-w-6xl mx-auto px-4 py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Business Header */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h1 className="text-3xl font-custom-bold text-gray-900">{entityData.name}</h1>
                                            {entityData.isVerified && (
                                                <CheckCircle className="w-6 h-6 text-blue-600"/>
                                            )}
                                        </div>
                                        <p className="text-gray-600 mb-2">Owned by {entityData.ownerName}</p>
                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                                  {entityData.category}
                                </span>
                                            <div className="flex items-center gap-1">
                                                <Award className="w-4 h-4"/>
                                                <span>{entityData.experience} experience</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Rating and Reviews */}
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star
                                                    key={star}
                                                    className={`w-5 h-5 ${
                                                        star <= Math.floor(entityData.rating)
                                                            ? 'text-yellow-400 fill-current'
                                                            : 'text-gray-300'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                        <span className="font-custom-semi-bold">{entityData.rating}</span>
                                        <span className="text-gray-600">({entityData.reviewCount} reviews)</span>
                                    </div>
                                    <div className={`flex items-center gap-1 text-sm font-custom-medium ${
                                        isOpenNow() ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        <Clock className="w-4 h-4"/>
                                        {isOpenNow() ? 'Open Now' : 'Closed'}
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="prose max-w-none">
                                    <p className="text-gray-700 leading-relaxed">{entityData.description}</p>
                                </div>
                            </div>

                            {/* Image Gallery */}
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div className="relative">
                                    <img
                                        src={entityData.images[currentImageIndex]}
                                        alt={`${entityData.name} - Image ${currentImageIndex + 1}`}
                                        className="w-full h-96 object-cover"
                                    />
                                    {entityData.images.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevImage}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                                            >
                                                <ChevronLeft className="w-5 h-5"/>
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                                            >
                                                <ChevronRight className="w-5 h-5"/>
                                            </button>
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                                {entityData.images.map((_, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => setCurrentImageIndex(index)}
                                                        className={`w-2 h-2 rounded-full transition-colors ${
                                                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Services/Products */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h2 className="text-xl font-custom-bold text-gray-900 mb-4">Services & Products</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {entityData.services.map((service, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0"/>
                                            <span className="text-gray-700">{service}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Special Offers */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h2 className="text-xl font-custom-bold text-gray-900 mb-4">Special Offers</h2>
                                <div className="space-y-3">
                                    {entityData.specialOffers.map((offer, index) => (
                                        <div key={index}
                                             className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                            <span className="text-gray-700">{offer}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Contact Information */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-custom-bold text-gray-900 mb-4">Contact Information</h3>
                                <div className="space-y-4">
                                    <a
                                        href={`tel:${entityData.mobile}`}
                                        className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
                                    >
                                        <Phone className="w-5 h-5 text-blue-600"/>
                                        <div>
                                            <p className="font-custom-medium text-gray-900 group-hover:text-blue-700">Call
                                                Now</p>
                                            <p className="text-sm text-gray-600">{entityData.mobile}</p>
                                        </div>
                                    </a>

                                    <a
                                        href={`https://wa.me/${entityData.whatsapp.replace(/[^0-9]/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
                                    >
                                        <MessageCircle className="w-5 h-5 text-green-600"/>
                                        <div>
                                            <p className="font-custom-medium text-gray-900 group-hover:text-green-700">WhatsApp</p>
                                            <p className="text-sm text-gray-600">{entityData.whatsapp}</p>
                                        </div>
                                    </a>

                                    {entityData.email && (
                                        <a
                                            href={`mailto:${entityData.email}`}
                                            className="flex items-center gap-3 p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors group"
                                        >
                                            <Mail className="w-5 h-5 text-purple-600"/>
                                            <div>
                                                <p className="font-custom-medium text-gray-900 group-hover:text-purple-700">Email</p>
                                                <p className="text-sm text-gray-600">{entityData.email}</p>
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Location */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-custom-bold text-gray-900 mb-4">Location</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-red-600 mt-1 flex-shrink-0"/>
                                        <div>
                                            <p className="text-gray-700">{entityData.address}</p>
                                            <p className="text-sm text-gray-500">{entityData.area}, {entityData.pinCode}</p>
                                        </div>
                                    </div>
                                    <button
                                        className="w-full bg-red-50 hover:bg-red-100 text-red-700 font-custom-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                                        <ExternalLink className="w-4 h-4"/>
                                        View on Maps
                                    </button>
                                </div>
                            </div>

                            {/* Operating Hours */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-custom-bold text-gray-900 mb-4">Operating Hours</h3>
                                <div className="space-y-2">
                                    {Object.entries(entityData.operatingHours).map(([day, hours]) => (
                                        <div key={day} className={`flex justify-between items-center py-2 ${
                                            day === getCurrentDay() ? 'bg-blue-50 px-3 rounded-lg font-custom-medium' : ''
                                        }`}>
                                            <span className="capitalize text-gray-700">{day}</span>
                                            <span
                                                className={`text-sm ${hours === 'Closed' ? 'text-red-600' : 'text-gray-600'}`}>
                          {hours}
                        </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-custom-bold text-gray-900 mb-4">Quick Info</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                        <Users className="w-6 h-6 text-blue-600 mx-auto mb-1"/>
                                        <p className="text-2xl font-custom-bold text-blue-700">{entityData.reviewCount}</p>
                                        <p className="text-xs text-gray-600">Reviews</p>
                                    </div>
                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                        <Award className="w-6 h-6 text-green-600 mx-auto mb-1"/>
                                        <p className="text-2xl font-custom-bold text-green-700">{entityData.rating}</p>
                                        <p className="text-xs text-gray-600">Rating</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
